import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { trackPageView } from '../../firebase/trackingService';
import SectionHeading from '../common/SectionHeading';
import Button from '../common/Button';
import AuthorSection from '../common/AuthorSection';

const AboutContainer = styled.section`
  padding: 120px 24px 80px;
  background-color: #121212;
  
  @media (max-width: 768px) {
    padding: 100px 24px 60px;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-top: 60px;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const StorySection = styled(motion.div)`
  color: #fff;
`;

const AuthorSectionWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  h3 {
    color: #2196F3;
    margin-bottom: 24px;
    font-size: 24px;
    text-align: center;
  }
`;

const MissionSection = styled.div`
  margin-top: 80px;
  background-color: #1E1E1E;
  padding: 60px;
  border-radius: 16px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 40px 24px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 24px;
  margin-bottom: 24px;
  color: #2196F3;
`;

const Paragraph = styled.p`
  font-size: 18px;
  line-height: 1.7;
  margin-bottom: 24px;
  color: rgba(255, 255, 255, 0.9);
`;



const CTAContainer = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: center;
`;

const SEOContentSection = styled.div`
  margin-top: 60px;
  text-align: left;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const SEOHeading = styled.h2`
  font-size: 28px;
  color: #2196F3;
  margin-bottom: 20px;
  font-weight: 700;
  text-align: center;
`;

const SEOText = styled.p`
  font-size: 17px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 20px;
  
  strong {
    color: #FFD700;
    font-weight: 600;
  }
`;

const AboutSection = () => {
  useEffect(() => {
    trackPageView('About', window.location.pathname);
  }, []);
  
  return (
    <AboutContainer id="about">
      <Content>
        <SectionHeading
          title="About CarConsul - Your Trusted Car Buying Advisor"
          subtitle="Revolutionizing the car buying experience with AI-powered negotiation tools and independent automotive consulting"
          alignment="center"
        />
        
        <AboutGrid>
          <StorySection
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <SectionTitle>The Car Buying Revolution</SectionTitle>
            <Paragraph>
              CarConsul was founded in 2025 as the world's first <strong>AI-powered car price negotiation app</strong> designed to level the playing field between consumers and dealerships. After witnessing countless car buyers overpay due to lack of information and negotiation expertise, our team of automotive industry veterans and AI specialists created a platform that puts professional-grade car buying advice in everyone's hands.
            </Paragraph>
            <Paragraph>
              What began as a <strong>car buying consultant AI</strong> has evolved into the most comprehensive vehicle purchasing platform available. Our proprietary algorithms analyze millions of data points including market trends, dealer inventories, regional pricing variations, and negotiation patterns to provide users with the exact strategies needed to secure the best possible deals.
            </Paragraph>
          </StorySection>
          
          <AuthorSectionWrapper
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3>Meet the Founder</h3>
            <AuthorSection showDescription={true} />
          </AuthorSectionWrapper>

        </AboutGrid>

        <MissionSection
          as={motion.div}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <SectionTitle>Democratizing Professional Car Buying Expertise</SectionTitle>
          <Paragraph>
            Our mission is to transform every car buyer into a skilled negotiator through our advanced <strong>car buying advisor platform</strong>. We believe that access to professional automotive consulting shouldn't be limited to those who can afford expensive personal consultants. CarConsul democratizes this expertise, providing the same level of strategic guidance at a fraction of the traditional cost.
          </Paragraph>
          <Paragraph>
            As a completely <strong>independent car buying consultant</strong>, we maintain zero relationships with dealerships, manufacturers, or financing companies. This independence ensures that every recommendation, negotiation strategy, and piece of advice serves only your interests. Our revenue comes exclusively from our users, allowing us to provide truly unbiased automotive consulting services.
          </Paragraph>
          <Paragraph>
            Through our <strong>AI car price negotiation platform</strong>, we've helped thousands of customers save an average of $3,200 per vehicle purchase while reducing the time spent negotiating by over 60%. Our goal is to make the car buying process transparent, efficient, and financially optimal for every consumer.
          </Paragraph>
          
          <CTAContainer>
            <Button 
              variant="primary" 
              size="large"
              id="about-cta-button"
              sectionName="about"
              onClick={() => window.location.href = 'https://app.carconsul.com'}
            >
              Try
            </Button>
          </CTAContainer>
        </MissionSection>
        
        <SEOContentSection>
          <SEOHeading>Why Choose CarConsul as Your Car Buying Advisor?</SEOHeading>
          <SEOText>
            Traditional car buying methods put consumers at a significant disadvantage. Dealerships employ professional negotiators, have access to real-time market data, and understand the psychological tactics that lead to higher purchase prices. CarConsul levels this playing field by giving you the same tools and strategies used by automotive industry professionals.
          </SEOText>
          <SEOText>
            Our <strong>car price negotiation app</strong> combines artificial intelligence with decades of automotive industry expertise to create personalized negotiation strategies. Whether you're buying new, used, or certified pre-owned, our platform analyzes your specific situation and provides step-by-step guidance to secure the best possible deal.
          </SEOText>
          <SEOText>
            Unlike traditional car buying services that earn commissions from dealers, CarConsul operates as a true <strong>independent car buying consultant</strong>. This means our success is measured solely by your savings and satisfaction, not by steering you toward particular dealers or financing arrangements that benefit us financially.
          </SEOText>
          <SEOText>
            Our team includes former automotive executives, professional car buyers, finance managers, and AI specialists who understand every aspect of the vehicle purchasing process. This expertise is distilled into our <strong>AI car buying advisor</strong> platform, giving you access to institutional-grade automotive consulting at consumer-friendly prices.
          </SEOText>
          <SEOText>
            Since launch, CarConsul has processed over 50,000 vehicle evaluations, negotiated millions in savings for our users, and maintained a 97% customer satisfaction rating. Join the growing community of smart car buyers who refuse to pay sticker price and demand transparency in their automotive transactions.
          </SEOText>
        </SEOContentSection>
      </Content>
    </AboutContainer>
  );
};

export default AboutSection;
